import styled from "@emotion/styled";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AttributeRepeatedTimeRangeComponent } from "@iventis/datafield-editor";
import { InteractiveElement, StyledFieldLabelLight } from "@iventis/styles";
import { MapObjectRepeatedTimeRangeValue, RepeatedTimeRangeAsDate } from "@iventis/types";
import { parseEpochSecondsToDate, parseTimeNumberToDate } from "@iventis/utilities";
import { IconButton } from "@mui/material";
import React, { useMemo, useState } from "react";
import { RepeatedTimeRangeModalComponent } from "@iventis/repeated-time-ranges";
import { InfoTooltip } from "@iventis/components";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { Content } from "@iventis/translations";

interface ObjectDetailsDataFieldRepeatedTimeRangeProps {
    name: string;
    dataFieldValue: MapObjectRepeatedTimeRangeValue[];
    mapObjectName: string;
    layerName: string;
    onAttributeSettingsClicked: (e: React.MouseEvent) => void;
    valueChanged: (value: MapObjectRepeatedTimeRangeValue[]) => void;
    canUserEdit: boolean;
}

export const ObjectDetailsDataFieldRepeatedTimeRangeComponent = ({
    name,
    mapObjectName,
    layerName,
    dataFieldValue = [],
    onAttributeSettingsClicked,
    valueChanged,
    canUserEdit,
}: ObjectDetailsDataFieldRepeatedTimeRangeProps) => {
    const [showModal, setShowModal] = useState(false);
    const [openHelp, setOpenHelp] = useState(false);
    const [saving, setSaving] = useState(false);
    const translate = useIventisTranslate();

    const objectDetailsTimeValues: RepeatedTimeRangeAsDate[] = useMemo(() => {
        if (dataFieldValue == null) {
            return [];
        }
        return dataFieldValue.map((value) => ({
            id: value.id,
            name: value.name,
            startDate: parseEpochSecondsToDate(value.startDate),
            endDate: parseEpochSecondsToDate(value.endDate),
            startTime: parseTimeNumberToDate(value.startTime),
            endTime: parseTimeNumberToDate(value.endTime),
        }));
    }, [dataFieldValue]);

    const handleSave = (values: MapObjectRepeatedTimeRangeValue[]) => {
        setSaving(true);
        valueChanged(values);
        setTimeout(() => {
            setSaving(false);
            setShowModal(false);
        }, 500);
    };

    return (
        <>
            <StyledFieldLabelLight className="object-data-field-label">
                <StyledLabelTooltipSpan>
                    {name}{" "}
                    <InfoTooltip
                        title={translate(Content.map8.dates.tooltip.title)}
                        text={<StyledSpan>{translate(Content.map8.dates.tooltip.blurb)}</StyledSpan>}
                        open={openHelp}
                        onClose={() => setOpenHelp(false)}
                        placement="right-end"
                    >
                        <InteractiveElement onClick={() => setOpenHelp(!openHelp)}>
                            <FontAwesomeIcon icon={["far", "circle-info"]} size="lg" />
                        </InteractiveElement>
                    </InfoTooltip>
                </StyledLabelTooltipSpan>
            </StyledFieldLabelLight>
            <RepeatedTimeRangeContainer>
                <AttributeRepeatedTimeRangeComponent attributeName={name} canUserEdit={canUserEdit} values={objectDetailsTimeValues} onAddTimeRange={() => setShowModal(true)} />
                {canUserEdit && (
                    <IconButton onClick={() => setShowModal(true)} data-testid="edit-repeated-time-range-value">
                        <FontAwesomeIcon size="sm" className="edit-icon" icon={["fas", "edit"]} />
                    </IconButton>
                )}
            </RepeatedTimeRangeContainer>
            {showModal && (
                <RepeatedTimeRangeModalComponent
                    mapObjectName={mapObjectName}
                    layerName={layerName}
                    attributeName={name}
                    initialValues={dataFieldValue ?? []}
                    saving={saving}
                    onSave={handleSave}
                    onClose={() => setShowModal(false)}
                    onAttributeSettingsClicked={onAttributeSettingsClicked}
                />
            )}
        </>
    );
};

const StyledSpan = styled.span`
    font-weight: normal;
`;

const StyledLabelTooltipSpan = styled.span`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 12px 12px 0;
`;

const RepeatedTimeRangeContainer = styled.div`
    width: 100%;
    display: flex;
    gap: 10px;
    justify-content: flex-start;
    align-items: flex-start;
    margin-bottom: 10px;

    .edit-icon {
        color: ${(props) => props.theme.typographyColors.blank};
    }
`;
